import React from "react";
import { Box, Divider, Paper, Stack, Tooltip, Typography } from "@mui/material";
import { formatMoney } from "utils/formatters/CurrencyFormatters";
import { AcceptanceMethod } from "client/api-v2";
import type { EntitlementConfiguration, OrderAcceptanceDto, OrderDto } from "client/api-v2";
import { MutedPlaceholder } from "components/MutedPlaceholder";
import { formatInstantInUtc } from "areas/orders/components/OrderVersionHistory";
import { ViaResellerBadge } from "areas/orders/components/chrome/ViaResellerBadge";
import { productPlatformLabels, selfServeLabel } from "areas/orders/orderConstants";
import { salesRepDisplayName } from "areas/orders/utils/salesRepDisplayName";
import { ENTITLEMENT_FIELD_ORDER } from "areas/subscriptions/constants/entitlement-field-order";
import { formatEntitlementValue, formatInclusionName } from "areas/subscriptions/utils/plan-utils";

const recordListSx = {
  display: "grid",
  gridTemplateColumns: "auto minmax(0, 1fr)",
  columnGap: 1.5,
  rowGap: 1.25,
  m: 0,
} as const;

const acceptanceMethodLabels: Record<AcceptanceMethod, string> = {
  [AcceptanceMethod.ClickThrough]: "Confirmed the terms",
  [AcceptanceMethod.SignedDocument]: "Signed an agreement",
};

function grantedEntitlements(entitlements: EntitlementConfiguration): Array<[string, unknown]> {
  const granted = Object.entries(entitlements).filter(([, value]) => value !== null && value !== undefined);
  const rank = (key: string) => {
    const index = ENTITLEMENT_FIELD_ORDER.findIndex((field) => field === key);
    return index < 0 ? ENTITLEMENT_FIELD_ORDER.length : index;
  };
  return granted.sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b));
}

function RecordRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <Typography component="dt" variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Box component="dd" sx={{ m: 0, minWidth: 0, textAlign: "right" }}>
        {children}
      </Box>
    </>
  );
}

export function OrderRecordCard({ order }: { order: OrderDto }) {
  // Why: codegen types both as non-null because Swashbuckle drops `nullable` from a $ref,
  // but the API sends null on every order that has not been accepted.
  const acceptance: OrderAcceptanceDto | null = order.acceptance;
  const entitlements: EntitlementConfiguration | null = order.entitlements;

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6" component="h2">
          Record
        </Typography>
        <Box component="dl" sx={recordListSx}>
          <RecordRow label="Quote reference">
            <Box component="span" sx={{ fontFamily: "monospace" }}>
              {order.odNumber}
            </Box>
          </RecordRow>
          <RecordRow label="Customer">
            <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", justifyContent: "flex-end" }}>
              <Typography variant="body2">{order.billingAccountLegalName}</Typography>
              {order.isViaReseller && (
                <ViaResellerBadge resellerLegalName={order.resellerBillingAccountLegalName ?? undefined} />
              )}
            </Stack>
          </RecordRow>
          <RecordRow label="Motion">
            <Typography variant="body2">{order.salesMotion}</Typography>
          </RecordRow>
          <RecordRow label="Licence type">
            <Typography variant="body2">{`${productPlatformLabels[order.platform]} · ${order.planName}`}</Typography>
          </RecordRow>
          <RecordRow label="Sales rep">
            {order.salesRep === null ? (
              <MutedPlaceholder>{selfServeLabel}</MutedPlaceholder>
            ) : (
              <Tooltip title={order.salesRepEmail ?? ""}>
                <Typography variant="body2">{salesRepDisplayName(order.salesRep)}</Typography>
              </Tooltip>
            )}
          </RecordRow>
          <RecordRow label="Total value">
            <Typography variant="body2">{formatMoney(order.total, order.currency)}</Typography>
          </RecordRow>
        </Box>
        {acceptance && (
          <>
            <Divider />
            <Typography variant="subtitle2" component="h3">
              Acceptance
            </Typography>
            <OrderAcceptanceFacts acceptance={acceptance} />
          </>
        )}
        {entitlements && <EntitlementSnapshot entitlements={entitlements} />}
      </Stack>
    </Paper>
  );
}

export function OrderAcceptanceFacts({ acceptance }: { acceptance: OrderAcceptanceDto }) {
  return (
    <Box component="dl" sx={recordListSx}>
      <RecordRow label="Accepted by">
        <Typography variant="body2">{acceptance.acceptedByName}</Typography>
      </RecordRow>
      <RecordRow label="For">
        <Typography variant="body2">{acceptance.acceptedByCompany}</Typography>
      </RecordRow>
      <RecordRow label="Accepted at">
        <Typography variant="body2">{`${formatInstantInUtc(acceptance.acceptedAt)} UTC`}</Typography>
      </RecordRow>
      <RecordRow label="Method">
        <Typography variant="body2">{acceptanceMethodLabels[acceptance.method]}</Typography>
      </RecordRow>
      {acceptance.recordedByStaffMember !== null && (
        <RecordRow label="Recorded by">
          <Typography variant="body2">{acceptance.recordedByStaffMember}</Typography>
        </RecordRow>
      )}
    </Box>
  );
}

function EntitlementSnapshot({ entitlements }: { entitlements: EntitlementConfiguration }) {
  return (
    <>
      <Divider />
      <Typography variant="subtitle2" component="h3">
        Entitlements frozen at acceptance
      </Typography>
      <Box component="dl" sx={recordListSx}>
        {grantedEntitlements(entitlements).map(([key, value]) => (
          <RecordRow key={key} label={formatInclusionName(key)}>
            <Typography variant="body2">{formatEntitlementValue(value)}</Typography>
          </RecordRow>
        ))}
      </Box>
    </>
  );
}
